import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Newspaper, ChevronRight, MapPin, RefreshCw } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageTitle } from "@/components/Layout";
import { NewsCard } from "@/components/NewsCard";

const SCOPES = [["all", "For you"], ["local", "Local"], ["national", "India"], ["global", "World"]];
const AUD = [["", "Everyone"], ["Business", "Business"], ["Household", "Household"], ["Government", "Government"]];
const SEEN = "ks_news_seen";
const CITY = "ks_city";
const PREF = "ks_news_scope";

const readCity = () => { try { return JSON.parse(localStorage.getItem(CITY) || "null"); } catch { return null; } };

const useCity = () => {
  const [city, setCity] = useState(() => readCity()?.city || "");
  const [busy, setBusy] = useState(false);
  const detect = useCallback((quiet) => {
    if (!navigator.geolocation) return !quiet && toast.error("GPS is not available on this device.");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (p) => {
      const { data } = await api.get("/news/city", { params: { lat: p.coords.latitude, lng: p.coords.longitude } }).catch(() => ({ data: { city: "Jaipur" } }));
      localStorage.setItem(CITY, JSON.stringify({ city: data.city, at: Date.now() }));
      setCity(data.city); setBusy(false);
    }, () => { setBusy(false); if (!quiet) toast.error("Couldn't get your location. Showing Jaipur news."); setCity((c) => c || "Jaipur"); },
    { timeout: 10000, maximumAge: 600000 });
  }, []);
  useEffect(() => { const c = readCity(); if (!c || Date.now() - c.at > 86400000) detect(true); }, [detect]);
  return { city, busy, detect };
};

const Chips = ({ items, value, onChange, prefix, dark }) => {
  const { t } = useApp();
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {items.map(([v, l]) => (
        <button key={l} onClick={() => onChange(v)} data-testid={`${prefix}-${l.toLowerCase().replace(/\s+/g, "-")}`}
          className={`shrink-0 rounded-full px-4 font-semibold transition-colors ${dark ? "h-10 text-sm" : "h-8 text-xs"} ${value === v ? (dark ? "bg-[#0D3B2B] text-white" : "bg-[#2E9E6B] text-white") : "border border-[#D2E0D8] bg-white text-[#4A6358]"}`}>{t(l)}</button>
      ))}
    </div>
  );
};

export default function News() {
  const { t } = useApp();
  const { city, busy, detect } = useCity();
  const [scope, setScopeState] = useState(() => localStorage.getItem(PREF) || "all");
  const [aud, setAud] = useState("");
  const [d, setD] = useState(null);
  const seenAt = useRef(localStorage.getItem(SEEN));
  const setScope = (s) => { localStorage.setItem(PREF, s); setScopeState(s); };
  const load = useCallback(() => api.get("/news", { params: { scope, city, audience: aud } }).then((r) => setD(r.data)).catch(() => setD({ items: [] })), [scope, city, aud]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!d?.refreshing) return; const id = setTimeout(load, 45000); return () => clearTimeout(id); }, [d, load]);
  useEffect(() => () => localStorage.setItem(SEEN, new Date().toISOString()), []);
  const refresh = async () => { await load(); toast.success(t("News is up to date")); };

  return (
    <div className="space-y-4">
      <PageTitle sub={t("Waste, recycling and cleanliness news near you, across India and the world. Newest first.")}>{t("News & rules")}</PageTitle>
      <div className="flex items-center justify-between gap-2 rounded-2xl bg-white p-3 shadow-sm">
        <button onClick={() => detect(false)} className="flex min-w-0 items-center gap-2 text-sm font-semibold text-[#0D3B2B]" data-testid="news-city-btn">
          <MapPin className="h-4 w-4 shrink-0 text-[#E05C3A]" /><span className="truncate" data-testid="news-city">{busy ? t("Finding your city…") : d?.city || city || "Jaipur"}</span>
          <span className="text-xs font-normal text-[#2E9E6B]">{t("Update")}</span>
        </button>
        <button onClick={refresh} className="flex items-center gap-1 text-xs text-[#7B968B]" data-testid="news-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${d?.refreshing ? "animate-spin" : ""}`} />{d?.updated_at ? timeAgo(d.updated_at, t) : t("Refresh")}
        </button>
      </div>
      <Chips items={SCOPES} value={scope} onChange={setScope} prefix="news-scope" dark />
      <Chips items={AUD} value={aud} onChange={setAud} prefix="news-filter" />
      {d?.refreshing && <p className="text-xs text-[#2E9E6B]" data-testid="news-refreshing">{t("Fetching the latest stories…")}</p>}
      <Link to="/compliance" className="flex items-center justify-between rounded-2xl bg-[#0D3B2B] p-4 font-bold text-white" data-testid="news-compliance-link">{t("What your business must do")}<ChevronRight className="h-5 w-5" /></Link>
      {d?.items.map((n) => <NewsCard key={n.news_id} n={n} isNew={!!seenAt.current && n.published_at > seenAt.current} />)}
      {d && !d.items.length && <Card className="flex items-center gap-3 text-[#4A6358]" data-testid="news-empty"><Newspaper className="h-5 w-5" />{d.refreshing ? t("We're collecting news for your area. Check back in a minute.") : t("No news here yet. Try another tab.")}</Card>}
      <p className="text-[11px] text-[#7B968B]">{t("Summaries are AI-generated from public news headlines. Please read the original story and official notices before acting.")}</p>
    </div>
  );
}
